/**
 * „Ich habe gekauft" — der Übergang von der Beobachtung zur Ausführung.
 *
 * Der Ablauf, für den er gebaut ist: Das System meldet eine offene Kaufzone.
 * Der Nutzer sieht sich den Wert an, kauft ihn bei Revolut, und trägt hier
 * ein, was er wirklich bezahlt hat. Ab diesem Moment ist der Trade in der App
 * ein echter Trade — mit Stop-Nachziehen, Teilverkauf und Ausstoppen über die
 * vorhandene V6-Mechanik (src/lib/v6/handlungen.ts).
 *
 * WAS HIER BEWUSST NICHT PASSIERT: Das Zonensignal wird NICHT angefasst. Es
 * läuft in `v5_zone_signals` unabhängig weiter und wird allein vom Server-Cron
 * aus den Kursen aufgelöst. Sonst hinge die Signalstatistik am Verhalten des
 * Nutzers, und die Frage „wie gut sind die Signale" wäre nicht mehr von „wie
 * gut folge ich ihnen" zu trennen. Genau diese Trennung ist der Zweck.
 *
 * Kein Broker-Import: Der Nutzer trägt absichtlich von Hand nach.
 */
import { useEffect, useMemo, useState } from 'react';
import { Check, X, AlertTriangle } from 'lucide-react';

import {
  insertPosition,
  nachkaufPosition,
  insertJournalEntry,
  fetchUserSettings,
  fetchPositions,
  fetchWatchlistEntries,
} from '@/lib/queries';
import type { UserSettings } from '@/lib/queries';
import type { Position } from '@/types';
import { berechneStueckzahl } from '@/lib/v6/uebernahme';
import { planePyramide, gewichteterEinstieg } from '@/lib/v6/pyramide';
import { pruefeRisiko, RISIKO_STANDARD, type PortfolioKontext } from '@/lib/v6/risiko';
import { fetchTageBisEarnings } from '@/lib/v6/earnings-read';
import { useSnapshots } from '@/hooks/use-snapshots';
import { deriveTradeCurrency, fxRate } from '@/lib/waehrung';
import { logCaughtError } from '@/lib/error-log';
import { cn } from '@/lib/utils';

export interface UebernahmeSignal {
  symbol: string;
  /** Obergrenze der Kaufzone — Vorbelegung für den Kaufpreis. */
  limit: number;
  stop: number;
  ausloeser?: string | null;
  /** id aus v5_zone_signals, wird zur Nachverfolgung mitgespeichert. */
  zoneSignalId?: number | null;
}

interface Props {
  signal: UebernahmeSignal;
  /**
   * Auf welche Spur gebucht wird (Audit 12.08.2026, Befund 3).
   *
   * `echt`  — bei Revolut wirklich gekauft. Risikoprüfung greift.
   * `papier`— dasselbe SIGNAL, nur auf Papier mitgenommen. Bindet kein Geld,
   *           zählt nicht ins Risikolimit, bleibt aber demselben Signal
   *           zugeordnet. Vorher gab es dafür nur den Experiment-Weg, und der
   *           bedeutet „ohne Systemvorschlag" — ein falsches Etikett.
   */
  spur: 'echt' | 'papier';
  onAbbruch: () => void;
  onGebucht: (positionId: string) => void;
}

function heute(): string {
  return new Date().toISOString().slice(0, 10);
}

export function UebernahmeDialog({ signal, spur, onAbbruch, onGebucht }: Props) {
  const aufPapier = spur === 'papier';
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [kaufpreis, setKaufpreis] = useState(String(signal.limit));
  const [stop, setStop] = useState(String(signal.stop));
  const [stueck, setStueck] = useState('');
  const [datum, setDatum] = useState(heute());
  const [gebuehren, setGebuehren] = useState('0');
  const [stueckAngefasst, setStueckAngefasst] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  /** Position steht, aber der Journaleintrag ist misslungen. */
  const [gebuchtOhneJournal, setGebuchtOhneJournal] = useState<string | null>(null);

  useEffect(() => {
    let ab = false;
    const ac = new AbortController();
    fetchUserSettings(ac.signal)
      .then((s) => { if (!ab) setSettings(s); })
      .catch(() => { if (!ab) setSettings(null); });
    return () => { ab = true; ac.abort(); };
  }, []);

  // P0-5 (11.08.2026): Das offene Gesamtrisiko wurde bisher nur ANGEZEIGT.
  // Hier wird es geprüft — und der Knopf bleibt aus, wenn es nicht passt.
  // Die eigentliche Sperre sitzt zusätzlich in der Datenbank (Trigger,
  // Migration 20260811_risiko_sperre): Ein Riegel, der nur im Browser hängt,
  // ist keiner.
  const [offene, setOffene] = useState<Position[] | null>(null);
  useEffect(() => {
    const ac = new AbortController();
    let ab = false;
    fetchPositions('open', ac.signal)
      .then((r) => { if (!ab) setOffene(r); })
      .catch(() => { if (!ab) setOffene([]); });
    return () => { ab = true; ac.abort(); };
  }, []);

  // Pyramiden-Einstieg (20.09.2026, Ronni: „More Crypto Online"/HKCM-Vorbild).
  // Läuft auf demselben Symbol UND derselben Spur schon eine offene Position,
  // ist dieser Dialog eine NACHKAUF-Tranche darauf statt eines neuen Trades —
  // gewichteter Durchschnittspreis statt einer zweiten Position. Broker-
  // Account trennt die Spuren hier genauso wie überall sonst (spuren.ts).
  const bestehendePosition = useMemo(
    () =>
      offene?.find(
        (p) =>
          p.symbol.toUpperCase() === signal.symbol.toUpperCase() &&
          p.broker_account === (aufPapier ? 'paper' : 'revolut')
      ) ?? null,
    [offene, signal.symbol, aufPapier]
  );
  const istNachkauf = bestehendePosition !== null;

  // §5.5 (12.08.2026) — Klumpen- und Earnings-Sperre. Die Daten werden HIER
  // beschafft und als Kontext in `pruefeRisiko` gereicht; die Entscheidung
  // selbst liegt in risiko.ts, damit Dialog und Datenbank-Trigger dieselbe
  // Regel befolgen.
  const [themenJeSymbol, setThemenJeSymbol] = useState<Record<string, string[]> | null>(null);
  const [neuThemen, setNeuThemen] = useState<string[] | null>(null);
  useEffect(() => {
    const ac = new AbortController();
    let ab = false;
    fetchWatchlistEntries(ac.signal)
      .then((eintraege) => {
        if (ab) return;
        const karte: Record<string, string[]> = {};
        for (const e of eintraege) karte[e.symbol.toUpperCase()] = [...(e.theme_tags ?? [])];
        setThemenJeSymbol(karte);
        setNeuThemen(karte[signal.symbol.toUpperCase()] ?? []);
      })
      .catch(() => {
        if (ab) return;
        // Ohne Watchlist keine Themen — die Prüfung meldet sich dann als
        // „nicht prüfbar", statt stillschweigend durchzuwinken.
        setThemenJeSymbol({});
        setNeuThemen([]);
      });
    return () => { ab = true; ac.abort(); };
  }, [signal.symbol]);

  // `undefined` = wird noch geladen, `null` = kein Termin bekannt.
  const [tageBisEarnings, setTageBisEarnings] = useState<number | null | undefined>(undefined);
  useEffect(() => {
    let ab = false;
    void fetchTageBisEarnings(signal.symbol)
      .then((t) => { if (!ab) setTageBisEarnings(t); })
      .catch(() => { if (!ab) setTageBisEarnings(null); });
    return () => { ab = true; };
  }, [signal.symbol]);

  // Die Korrelation wird NICHT hier gerechnet, sondern aus der Tagesakte
  // gelesen: Der Cron hat sie einmal gegen den echten Bestand ermittelt, und
  // der Datenbank-Trigger liest dieselbe Zeile. Zwei Rechnungen ergaeben zwei
  // Antworten auf dieselbe Frage.
  const snapshots = useSnapshots();
  const maxKorrelation = useMemo(() => {
    const zeile = snapshots.bySymbol.get(signal.symbol.toUpperCase());
    const p = zeile?.portfolio_risk_check;
    if (!p || typeof p.max_korrelation !== 'number') return null;
    return { wert: p.max_korrelation, gegen: p.gegen };
  }, [snapshots.bySymbol, signal.symbol]);

  const kontext = useMemo<PortfolioKontext | null>(() => {
    if (themenJeSymbol === null || neuThemen === null || tageBisEarnings === undefined) return null;
    if (snapshots.isLoading) return null;
    return { neuThemen, themenJeSymbol, tageBisEarnings, maxKorrelation };
  }, [themenJeSymbol, neuThemen, tageBisEarnings, maxKorrelation, snapshots.isLoading]);

  const handelsWaehrung = useMemo(
    () => deriveTradeCurrency(signal.symbol, null),
    [signal.symbol]
  );
  const kontoWaehrung = settings?.account_currency ?? 'EUR';
  const kurs = useMemo(
    () => fxRate(handelsWaehrung, kontoWaehrung),
    [handelsWaehrung, kontoWaehrung]
  );

  const vorschlag = useMemo(
    () =>
      berechneStueckzahl({
        einstieg: Number(kaufpreis.replace(',', '.')),
        stop: Number(stop.replace(',', '.')),
        kontogroesse: settings?.account_balance ?? 0,
        risikoProzent: settings?.default_risk_per_trade_pct ?? 0,
        fxRate: kurs,
        bruchstueckeErlaubt: true,
      }),
    [kaufpreis, stop, settings, kurs]
  );

  // Ein Nachkauf teilt sich den Stop mit der bestehenden Position — sonst ist
  // es kein Pyramiden-Tranchen-Kauf mehr, sondern ein neuer, unabhängiger
  // Trade (dafür gibt es insertPosition). Feld wird deshalb erzwungen, nicht
  // nur vorbelegt.
  useEffect(() => {
    if (bestehendePosition) setStop(String(bestehendePosition.current_stop));
  }, [bestehendePosition]);

  // Pyramiden-Vorschlag — reine Anzeige/Rechenhilfe, bindend ist nur, was der
  // Nutzer am Ende ins Feld einträgt. Preise kommen aus der ECHTEN Kaufzone
  // (signal.limit/signal.stop, aus setup-state.ts), nie aus der Elliott-
  // Hypothese (Regel 1). Fünf Tranchen, weil Ronni genau danach gefragt hat
  // („die erste, die zweite, ... die fünfte").
  const pyramide = useMemo(() => {
    if (!settings || aufPapier) return null;
    return planePyramide({
      zoneOben: signal.limit,
      stop: signal.stop,
      kontogroesse: settings.account_balance,
      risikoProzentGesamt: settings.default_risk_per_trade_pct,
      fxRate: kurs,
      anzahlTranchen: 5,
      bruchstueckeErlaubt: true,
    });
  }, [settings, aufPapier, signal.limit, signal.stop, kurs]);

  // Wie die Position nach DIESER Tranche aussähe — für die Anzeige, nicht für
  // die Buchung selbst (die passiert serverseitig identisch in
  // nachkaufPosition, damit Anzeige und Schreibvorgang nicht auseinanderlaufen
  // können).
  const nachDieserTranche = useMemo(() => {
    if (!bestehendePosition) return null;
    const stueckNeu = Number(stueck.replace(',', '.'));
    const einstiegNeu = Number(kaufpreis.replace(',', '.'));
    if (!(stueckNeu > 0) || !(einstiegNeu > 0)) return null;
    return {
      quantity: bestehendePosition.quantity + stueckNeu,
      avg_entry_price: gewichteterEinstieg(
        bestehendePosition.quantity,
        bestehendePosition.avg_entry_price,
        stueckNeu,
        einstiegNeu
      ),
    };
  }, [bestehendePosition, stueck, kaufpreis]);

  const risiko = useMemo(() => {
    // Auf Papier gibt es nichts zu sperren: Es wird kein Geld gebunden, kein
    // Themenklumpen gebildet und kein Earnings-Risiko eingegangen. Eine
    // Sperre hier wuerde das Mitschreiben des Signals verhindern — also genau
    // die Messung, um die es geht.
    if (aufPapier) return null;
    // Solange EINE der Quellen fehlt, gibt es kein Urteil — und damit keinen
    // aktiven Knopf. Lieber eine Sekunde warten als an einer Sperre vorbei
    // buchen, die noch lud.
    if (offene === null || !settings || kontext === null) return null;
    return pruefeRisiko(
      offene.map((p) => ({
        symbol: p.symbol,
        quantity: p.quantity,
        avg_entry_price: p.avg_entry_price,
        current_stop: p.current_stop,
        fx_rate_at_entry: p.fx_rate_at_entry,
        broker_account: p.broker_account,
        created_by: p.created_by,
      })),
      {
        einstieg: Number(kaufpreis.replace(',', '.')),
        stop: Number(stop.replace(',', '.')),
        stueck: Number(stueck.replace(',', '.')),
        fxRate: kurs,
      },
      {
        kontogroesse: settings.account_balance,
        maxGesamtRisikoPct: settings.max_total_open_risk_pct,
        // Die gesetzten Grenzen stehen gesammelt in risiko.ts, mit Begründung
        // je Zahl. Hier stand bis zum 12.08. eine abgeschriebene 6.
        ...RISIKO_STANDARD,
      },
      kontext
    );
  }, [offene, settings, kaufpreis, stop, stueck, kurs, kontext]);

  // Vorschlag übernehmen, solange der Nutzer das Feld nicht selbst angefasst
  // hat. Danach nie wieder überschreiben — sonst tippt man gegen die App an.
  useEffect(() => {
    if (!stueckAngefasst && vorschlag.stueck > 0) setStueck(String(vorschlag.stueck));
  }, [vorschlag.stueck, stueckAngefasst]);

  const zahl = (s: string) => Number(s.replace(',', '.'));
  const preisNum = zahl(kaufpreis);
  const stopNum = zahl(stop);
  const stueckNum = zahl(stueck);
  const bereit =
    preisNum > 0 && stopNum > 0 && stopNum < preisNum && stueckNum > 0 && !laeuft
    // Solange die Prüfung noch lädt (risiko === null), bleibt der Knopf aus.
    // Lieber eine Sekunde warten als eine Buchung am Limit vorbei.
    // Auf Papier entfaellt die Risikopruefung (siehe oben), also auch ihr
    // Riegel am Knopf.
    && (aufPapier || (risiko !== null && risiko.erlaubt));

  async function buchen() {
    if (!bereit) return;
    setLaeuft(true);
    setFehler(null);
    try {
      let positionId: string;
      if (bestehendePosition) {
        // Nachkauf-Tranche (Pyramiden-Einstieg) — dieselbe Position wächst,
        // statt dass eine zweite entsteht. Der Stop ist überall in diesem
        // Dialog auf bestehendePosition.current_stop gesperrt (siehe
        // useEffect oben), deshalb bleibt die Risikorechnung additiv korrekt.
        positionId = bestehendePosition.id;
        await nachkaufPosition(positionId, {
          stueck: stueckNum,
          einstieg: preisNum,
          fxRate: kurs,
          gebuehren: zahl(gebuehren) || 0,
        });
      } else {
        positionId = await insertPosition({
          trade_plan_id: null,
          broker: 'revolut',
          // DIE eine Zeile, die die zwei Spuren trennt. Die Paper-Engine
          // verwaltet nur 'paper' (src/lib/paper-engine.ts) und lässt diese
          // Position in Ruhe — sie gehört dem Broker, nicht der Simulation.
          broker_account: aufPapier ? 'paper' : 'revolut',
          symbol: signal.symbol,
          // Zonensignale sind ausschliesslich Aktien und ETFs: Der Server-Cron
          // erzeugt für Symbole mit „/" keine (server-score-cron.ts, Krypto-Riegel).
          asset_class: 'stock',
          side: 'long',
          quantity: stueckNum,
          avg_entry_price: preisNum,
          entry_at: new Date(datum).toISOString(),
          entry_fees: zahl(gebuehren) || 0,
          trade_currency: handelsWaehrung,
          account_currency: kontoWaehrung,
          fx_rate_at_entry: kurs,
          current_stop: stopNum,
          // initial_stop definiert das R und darf sich nie ändern —
          // src/lib/v6/handlungen.ts rechnet den Teilverkauf dagegen.
          initial_stop: stopNum,
          take_profit_1: null,
          take_profit_2: null,
          take_profit_3: null,
          created_by: aufPapier ? 'signal_papier' : 'manual',
        });
      }

      // Einstiegs-Eintrag ins Journal. Der Audit vom 10.08. hat gezeigt, dass
      // von fünf Ausstiegswegen nur einer eine Zeile schreibt — dieser neue
      // Weg soll nicht die sechste Lücke werden.
      try {
        await insertJournalEntry({
          position_id: positionId,
          trade_plan_id: null,
          date: datum,
          action_type: bestehendePosition ? 'adjustment' : 'entry',
          title: bestehendePosition
            ? `Nachkauf-Tranche: ${signal.symbol}`
            : `Übernommen: ${signal.symbol}`,
          body_markdown: (bestehendePosition
            ? [
                `**Pyramiden-Tranche auf eine bestehende ${aufPapier ? 'Papier-' : ''}Position.**`,
                '',
                `- Vorher: ${bestehendePosition.quantity} Stück @ ⌀ ${bestehendePosition.avg_entry_price.toFixed(2)} ${handelsWaehrung}`,
                `- Diese Tranche: ${stueckNum} Stück @ ${preisNum.toFixed(2)} ${handelsWaehrung}`,
                nachDieserTranche
                  ? `- Danach: ${nachDieserTranche.quantity} Stück @ ⌀ ${nachDieserTranche.avg_entry_price.toFixed(2)} ${handelsWaehrung}`
                  : '',
                `- Stop unverändert: ${stopNum.toFixed(2)} (derselbe Stop für alle Tranchen)`,
              ]
            : [
                aufPapier
                  ? '**Aus einem Zonensignal auf PAPIER mitgenommen — kein echtes Geld.**'
                  : '**Aus einem Zonensignal übernommen und bei Revolut ausgeführt.**',
                '',
                `- Auslöser: ${signal.ausloeser ?? '—'}`,
                `- Kaufzone laut System: bis ${signal.limit.toFixed(2)} ${handelsWaehrung}`,
                `- Tatsächlich bezahlt: ${preisNum.toFixed(2)} ${handelsWaehrung}`,
                // Risiko aus der TATSAECHLICH gebuchten Stueckzahl. Vorher stand
                // hier vorschlag.risikoBetrag — also das Risiko der VORGESCHLAGENEN
                // Groesse. Wer die Stueckzahl ueberschrieb, bekam eine Zahl ins
                // Journal, die nicht zur Position passte; ohne geladene
                // Einstellungen sogar 0. Beim Review gefunden.
                `- Stop: ${stopNum.toFixed(2)} (Risiko `
                  + `${((preisNum - stopNum) * stueckNum * kurs).toFixed(0)} ${kontoWaehrung})`,
                `- Stückzahl: ${stueckNum}${
                  vorschlag.stueck > 0 && Math.abs(stueckNum - vorschlag.stueck) > 1e-9
                    ? ` (Vorschlag war ${vorschlag.stueck})`
                    : ''
                }`,
              ]
          )
            .concat([
              signal.zoneSignalId != null ? `- Zonensignal: #${signal.zoneSignalId}` : '',
              '',
              '**Warum dieser Trade?** _Bitte ergänzen._',
            ])
            .filter(Boolean)
            .join('\n'),
          setup_type: null,
          // 'transition' ist der neutrale Wert, den auch der manuelle
          // Journal-Dialog vorbelegt (AddJournalDialog.tsx:92). Die Marktphase
          // hier automatisch zu raten waere eine Behauptung ohne Grundlage.
          market_phase: 'transition',
          rule_followed: true,
          discipline_score: 0,
          emotional_state: null,
          error_tags: [],
          positive_tags: [],
          plan_vs_execution: null,
          attached_chart_url: null,
        });
      } catch (e) {
        // Die Position steht bereits — ein fehlender Journaleintrag darf den
        // Kauf nicht rückgängig machen. Aber er wird gemeldet, nicht
        // verschluckt: genau dieses stille Verschlucken war ein Audit-Befund.
        logCaughtError('uebernahme.journal', e, { severity: 'warn' });
        // NICHT schliessen: Der Dialog bleibt stehen und meldet, dass die
        // Position zwar gebucht ist, der Journaleintrag aber fehlt. Vorher
        // wurde direkt danach onGebucht aufgerufen, der Dialog verschwand,
        // und die Warnung hat nie jemand gesehen. Beim Review gefunden.
        setLaeuft(false);
        setGebuchtOhneJournal(positionId);
        return;
      }

      onGebucht(positionId);
    } catch (e) {
      setFehler(`Konnte nicht buchen: ${(e as Error).message}`);
      logCaughtError('uebernahme.position', e, { severity: 'error' });
    } finally {
      setLaeuft(false);
    }
  }

  const Feld = ({
    label,
    wert,
    setzen,
    zusatz,
    typ = 'text',
  }: {
    label: string;
    wert: string;
    setzen: (v: string) => void;
    zusatz?: string;
    typ?: string;
  }) => (
    <label className="block space-y-1">
      <span className="text-[11px] text-zinc-400">
        {label}
        {zusatz && <span className="text-zinc-600"> · {zusatz}</span>}
      </span>
      <input
        type={typ}
        inputMode={typ === 'text' ? 'decimal' : undefined}
        value={wert}
        onChange={(e) => setzen(e.target.value)}
        className="w-full rounded border border-zinc-700 bg-zinc-900/60 px-2 py-1.5
                   text-[13px] tabular-nums focus:outline-none focus:border-zinc-500"
      />
    </label>
  );

  // Die Position IST gebucht, nur das Protokoll fehlt. Diese Ansicht sagt
  // beides und laesst den Nutzer bewusst bestaetigen, statt still zu schliessen.
  if (gebuchtOhneJournal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-md cockpit-card p-4 space-y-3">
          <h2 className="text-sm font-semibold">
            {signal.symbol} ist gebucht — der Journaleintrag fehlt
          </h2>
          <p className="text-[12px] text-zinc-400 leading-snug">
            Die Position steht im Depot und wird ab sofort überwacht. Nur der
            automatische Journaleintrag ist fehlgeschlagen. Du kannst ihn im
            Journal von Hand nachtragen — die Position ist davon unberührt.
          </p>
          <div className="flex justify-end">
            <button
              onClick={() => onGebucht(gebuchtOhneJournal)}
              className="rounded border border-zinc-600 px-3 py-1.5 text-[13px]
                         text-zinc-200 hover:bg-zinc-800"
            >
              Verstanden
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md cockpit-card p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold">
              {signal.symbol}{' '}
              {istNachkauf
                ? 'Tranche nachtragen'
                : aufPapier
                  ? 'auf Papier mitnehmen'
                  : 'übernehmen'}
            </h2>
            <p className="text-[11px] text-zinc-500">
              {istNachkauf
                ? `Läuft schon: ${bestehendePosition!.quantity} Stück @ ⌀ `
                  + `${bestehendePosition!.avg_entry_price.toFixed(2)} ${handelsWaehrung}. `
                  + 'Stop bleibt für alle Tranchen derselbe.'
                : aufPapier
                  ? 'Kein echtes Geld. Teilverkauf und Stop führt der Server '
                    + 'automatisch nach der Regel aus — du musst nichts tun.'
                  : 'Trag ein, was du bei Revolut wirklich bezahlt hast.'}
            </p>
          </div>
          <button
            onClick={onAbbruch}
            className="text-zinc-500 hover:text-zinc-300"
            aria-label="Abbrechen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Pyramiden-Vorschlag: reine Anzeige, klickbar zum Übernehmen ins
            Formular. Preise aus der ECHTEN Kaufzone (Regel 1) — nicht aus
            Elliott. Nur bei echtem Geld gezeigt (auf Papier ohnehin kein
            Risikobudget zu verteilen). */}
        {pyramide && (
          <div className="rounded border border-zinc-800/60 overflow-hidden">
            <div className="px-3 pt-2 text-[11px] text-zinc-400">
              Pyramide — {pyramide.tranchen.length} Tranchen, je{' '}
              {pyramide.risikoJeTranchePct.toFixed(3)} % Risiko (
              {pyramide.gesamtRisiko.toFixed(0)} {kontoWaehrung} gesamt, wie ein
              Einzeleinstieg)
            </div>
            <table className="w-full text-[11px] tabular-nums">
              <tbody>
                {pyramide.tranchen.map((t) => (
                  <tr
                    key={t.nummer}
                    className="cursor-pointer border-t border-zinc-800/50 hover:bg-zinc-800/40"
                    onClick={() => {
                      setKaufpreis(String(t.einstieg));
                      setStueckAngefasst(true);
                      setStueck(String(t.stueck));
                    }}
                  >
                    <td className="px-3 py-1 text-zinc-500">#{t.nummer}</td>
                    <td className="px-2 py-1">{t.einstieg.toFixed(2)}</td>
                    <td className="px-2 py-1 text-zinc-400">{t.stueck} Stk.</td>
                    <td className="px-2 py-1 text-zinc-400">
                      {t.kapitalBindung.toFixed(0)} {kontoWaehrung}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {Feld({ label: 'Kaufpreis', wert: kaufpreis, setzen: setKaufpreis, zusatz: handelsWaehrung })}
          {istNachkauf ? (
            <label className="block space-y-1">
              <span className="text-[11px] text-zinc-400">
                Stop <span className="text-zinc-600">· fix, teilt sich mit der Position</span>
              </span>
              <input
                type="text"
                value={stop}
                disabled
                className="w-full rounded border border-zinc-800 bg-zinc-900/30 px-2 py-1.5
                           text-[13px] tabular-nums text-zinc-500 cursor-not-allowed"
              />
            </label>
          ) : (
            Feld({ label: 'Stop', wert: stop, setzen: setStop, zusatz: handelsWaehrung })
          )}
          <label className="block space-y-1">
            <span className="text-[11px] text-zinc-400">
              Stückzahl
              {vorschlag.stueck > 0 && (
                <span className="text-zinc-600"> · Vorschlag {vorschlag.stueck}</span>
              )}
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={stueck}
              onChange={(e) => { setStueckAngefasst(true); setStueck(e.target.value); }}
              className="w-full rounded border border-zinc-700 bg-zinc-900/60 px-2 py-1.5
                         text-[13px] tabular-nums focus:outline-none focus:border-zinc-500"
            />
          </label>
          {Feld({ label: 'Gebühren', wert: gebuehren, setzen: setGebuehren, zusatz: kontoWaehrung })}
          <div className="col-span-2">
            {Feld({ label: 'Kaufdatum', wert: datum, setzen: setDatum, typ: 'date' })}
          </div>
        </div>

        {nachDieserTranche && (
          <p className="text-[11px] text-zinc-400">
            Danach: {nachDieserTranche.quantity} Stück @ ⌀{' '}
            {nachDieserTranche.avg_entry_price.toFixed(2)} {handelsWaehrung}
          </p>
        )}

        {/* Was der Trade kostet, wenn er schiefgeht — die einzige Zahl, die
            beim Kauf wirklich zählt. */}
        {stueckNum > 0 && stopNum > 0 && stopNum < preisNum && (
          <div className="rounded border border-zinc-800/60 px-3 py-2 text-[11px] space-y-0.5">
            <div className="flex justify-between">
              <span className="text-zinc-400">Risiko bis zum Stop</span>
              <span className="tabular-nums text-zinc-200">
                {((preisNum - stopNum) * stueckNum * kurs).toFixed(0)} {kontoWaehrung}
                {settings?.account_balance
                  ? ` · ${(((preisNum - stopNum) * stueckNum * kurs) / settings.account_balance * 100).toFixed(2)} % des Kontos`
                  : ''}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Gebundenes Kapital</span>
              <span className="tabular-nums text-zinc-200">
                {(preisNum * stueckNum * kurs).toFixed(0)} {kontoWaehrung}
              </span>
            </div>
            {risiko && (
              <div className="flex justify-between border-t border-zinc-800/60 pt-1 mt-1">
                <span className="text-zinc-400">Offenes Risiko danach</span>
                <span
                  className={cn(
                    'tabular-nums',
                    risiko.erlaubt ? 'text-zinc-200' : 'text-rose-300'
                  )}
                >
                  {risiko.gesamtPct.toFixed(2)} %
                  <span className="text-zinc-500">
                    {' '}
                    von {settings?.max_total_open_risk_pct.toFixed(1)} % ·{' '}
                    {risiko.bestehendeAnzahl} offen
                  </span>
                </span>
              </div>
            )}
          </div>
        )}

        {/* Die Sperre — sichtbar mit Grund, nicht als ausgegrauter Knopf ohne
            Erklärung. Wer nicht buchen darf, soll wissen warum und was hilft. */}
        {risiko && !risiko.erlaubt && risiko.gruende.length > 0 && (
          <div className="rounded border border-rose-500/40 bg-rose-500/5 px-3 py-2 space-y-1">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-rose-200">
              <AlertTriangle className="w-3.5 h-3.5" />
              Buchung gesperrt
            </div>
            {risiko.gruende.map((g) => (
              <p key={g} className="text-[11px] text-rose-200/85 leading-snug">
                {g}
              </p>
            ))}
          </div>
        )}

        {/* Was auffällt, ohne zu sperren. Bewusst in einer eigenen Box und
            NICHT rot: Ein Hinweis, der wie eine Sperre aussieht, erzieht dazu,
            auch die echten Sperren zu überlesen. */}
        {risiko && risiko.hinweise.length > 0 && (
          <div className="rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 space-y-1">
            {risiko.hinweise.map((hinweis) => (
              <p key={hinweis} className="text-[11px] text-amber-200/85 leading-snug">
                {hinweis}
              </p>
            ))}
          </div>
        )}

        {/* Was NICHT geprüft werden konnte. Gehört sichtbar hierher: Ein
            durchgelassener Kauf ohne Themen- oder Termin-Angabe ist nicht
            dasselbe wie ein geprüfter — und der Unterschied darf nicht am
            Fehlen einer roten Box hängen. */}
        {risiko && risiko.nicht_pruefbar.length > 0 && (
          <p className="text-[11px] text-amber-300/80 leading-snug">
            Nicht geprüft:{' '}
            {risiko.nicht_pruefbar
              .map((n) =>
                n === 'thema_unbekannt'
                  ? 'Klumpenrisiko — für diesen Wert ist kein Thema hinterlegt'
                  : n === 'earnings_unbekannt'
                    ? 'Earnings — für diesen Wert ist kein Termin bekannt'
                    : 'Ähnlichkeit zum Bestand — für diesen Wert liegt noch keine Tagesakte vor'
              )
              .join(' · ')}
            .
          </p>
        )}

        {vorschlag.hinweis && (
          <p className="flex gap-1.5 text-[11px] text-amber-300/90">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{vorschlag.hinweis}</span>
          </p>
        )}
        {fehler && <p className="text-[11px] text-rose-300">{fehler}</p>}

        <div className="flex items-center justify-between gap-2 pt-1">
          <p className="text-[10px] text-zinc-600 leading-snug">
            Das Zonensignal läuft unabhängig weiter — es misst die Signalgüte,
            diese Position misst deine Ausführung.
          </p>
          <button
            onClick={buchen}
            disabled={!bereit}
            className={cn(
              'shrink-0 inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-medium',
              bereit
                ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/25'
                : 'bg-zinc-800/50 text-zinc-500 border border-zinc-700/50 cursor-not-allowed'
            )}
          >
            <Check className="w-4 h-4" />
            {laeuft ? 'Buche…' : 'Gekauft'}
          </button>
        </div>
      </div>
    </div>
  );
}
